import { useState } from 'owa-react';
import { twistyButtonExpandConversationLabel } from './MailListItemTwistyButton.locstring.json';
import {
    twistyImage,
    twistyImageFull,
    twistyImageFirstLevelRotation,
    twistyImageSecondLevelRotation,
    loadingItemPartsSpinnerSingleLine,
    loadingItemPartsSpinnerThreeColumn,
    twistyMCL,
    twistyMCLMessageSelected,
    twistySCL,
    twistySCLMessageSelected,
    twistyUnread,
    twistyUnreadMessageSelected,
    sclTwistyIconButtonContainer,
    button,
} from './MailListItemTwistyButton.scss';
/* eslint-disable-next-line @fluentui/react-components/prefer-fluentui-v9 -- (https://aka.ms/OWALintWiki)
 * Baseline. DO NOT COPY AND PASTE! Substitute this line with an explanation or resolve the problem by using v9 components instead
 *	> Avoid importing Spinner from '@fluentui/react', as this package has started migration to Fluent UI 9. Import Spinner from '@fluentui/react-components' instead. */
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import classnames from 'owa-classnames';
import type { DensityModeString } from 'owa-fabric-theme';
import ChevronUpRegular from 'owa-fluent-icons-svg/lib/icons/ChevronUpRegular';
import { FluentButton } from 'owa-fluent-v9-shims';
import loc from 'owa-localize';
import type { ITouchHandlerParams } from 'owa-touch-handler';
import { touchHandler } from 'owa-touch-handler';
import {
    onMailListItemClickHandler,
    useMailListItemContainerContext,
} from 'owa-mail-list-item-shared';
import MailListItemSelectionSource from 'owa-mail-store/lib/store/schema/MailListItemSelectionSource';
import {
    getConditionalFormattingStyles,
    ConditionalFormattingSurface,
} from 'owa-conditional-formatting';
import type { IIconProps } from '@fluentui/react/lib/Icon';
import React from 'react';
import getListViewState from 'owa-mail-list-store/lib/selectors/getListViewState';

export interface MailListItemTwistyButtonProps {
    densityMode: DensityModeString;
    isDarkTheme: boolean;
    isLoadingSecondLevelExpansion: boolean;
    isFirstLevelExpanded: boolean;
    renderMinimum: boolean;
    isSecondLevelExpanded: boolean;
    isSelected: boolean;
    isSingleLine: boolean;
    isUnread: boolean;
    rowKey: string;
    tableViewId: string;
}

export default function MailListItemTwistyButton(
    props: MailListItemTwistyButtonProps
): JSX.Element {
    const {
        densityMode,
        isDarkTheme,
        isLoadingSecondLevelExpansion,
        isFirstLevelExpanded,
        renderMinimum,
        isSecondLevelExpanded,
        isSelected,
        isSingleLine,
        isUnread,
        rowKey,
        tableViewId,
    } = props;

    const [isLoadingExpansion, setIsLoadingExpansion] = useState(false);

    React.useEffect(() => {
        let delayShowingSpinner: number | undefined;
        if (!renderMinimum) {
            delayShowingSpinner = self.setTimeout(() => {
                const isLoading =
                    getListViewState().expandedConversationViewState?.expandedRowKey === rowKey &&
                    isLoadingSecondLevelExpansion;
                /* eslint-disable-next-line owa-custom-rules/forbid-react-set-state-and-mutators-inside-specific-hooks -- (https://aka.ms/OWALintWiki)
                 * BASELINE. DO NOT COPY AND PASTE!, replace this line with a justification or fix the error.
                 *	> Function 'setIsLoadingExpansion' cannot be called inside 'useEffect' to avoid unnecessary re-renders */
                setIsLoadingExpansion(isLoading);
            }, 500);
        }

        return () => {
            if (typeof delayShowingSpinner === 'number') {
                self.clearTimeout(delayShowingSpinner);
            }
        };
    }, [isLoadingSecondLevelExpansion, rowKey]);

    const { conditionalFormattingModifications } = useMailListItemContainerContext();
    const twistyConditionalFormattingStyles = React.useMemo(() => {
        const conditionalFormattingStyles =
            conditionalFormattingModifications && conditionalFormattingModifications.length > 0
                ? getConditionalFormattingStyles(
                      ConditionalFormattingSurface.Icon,
                      conditionalFormattingModifications,
                      isDarkTheme,
                      isSelected
                  )
                : undefined;

        return conditionalFormattingStyles;
    }, [conditionalFormattingModifications, isDarkTheme, isSelected]);

    const touchHandler_0 = {
        get() {
            const touchHandlerParams: ITouchHandlerParams = {
                onClick: (evt: React.TouchEvent<unknown>) => {
                    onClick(evt);
                    evt.stopPropagation();
                    evt.preventDefault();
                },
            };
            return touchHandler(touchHandlerParams);
        },
    };

    // unneeded in spinner case, but hooks must be defined prior to any return statements
    const onClick = React.useCallback(
        (evt: React.MouseEvent<unknown> | React.TouchEvent<unknown>) => {
            onMailListItemClickHandler(
                evt,
                MailListItemSelectionSource.MailListItemTwisty,
                rowKey,
                tableViewId
            );
        },
        [rowKey, tableViewId]
    );

    const secondLevelExpansion = isSecondLevelExpanded || isLoadingSecondLevelExpansion;
    const isFullDensity = densityMode === 'full';

    const iconRootStyles = classnames(
        twistyImage,
        // Rotates chevron by 45 degrees for first level expansion
        isFirstLevelExpanded && !secondLevelExpansion && twistyImageFirstLevelRotation,
        // Rotates chevron downwards for second level expansion or if only one level of expansion exists.
        secondLevelExpansion && twistyImageSecondLevelRotation,
        isFullDensity && twistyImageFull
    );

    const chevronIconProps: IIconProps = React.useMemo(
        () => ({
            iconName: ChevronUpRegular,
            styles: {
                root: iconRootStyles,
            },
        }),
        [iconRootStyles]
    );

    // show spinner if loading expansion
    if (isLoadingExpansion) {
        return (
            <Spinner
                // need to stop the event from bubbling up and causing the popout view
                onDoubleClick={stopPropagation}
                className={classnames(
                    isSingleLine
                        ? loadingItemPartsSpinnerSingleLine
                        : loadingItemPartsSpinnerThreeColumn
                )}
                size={SpinnerSize.xSmall}
            />
        );
    }

    const iconButtonClasses = classnames(
        isSingleLine
            ? classnames(twistyMCL, isSelected && twistyMCLMessageSelected)
            : classnames(twistySCL, isSelected && twistySCLMessageSelected),
        isUnread &&
            !twistyConditionalFormattingStyles &&
            (isSelected ? twistyUnreadMessageSelected : twistyUnread)
    );

    return (
        <div
            className={!isSingleLine ? sclTwistyIconButtonContainer : undefined}
            {...touchHandler_0.get()}
        >
            <FluentButton
                appearance="icon"
                aria-expanded={isSecondLevelExpanded}
                aria-label={loc(twistyButtonExpandConversationLabel)}
                className={classnames(iconButtonClasses, button)}
                iconProps={chevronIconProps}
                tabIndex={-1}
                onClick={onClick}
                onDoubleClick={stopPropagation}
                style={twistyConditionalFormattingStyles}
            />
        </div>
    );
}

const stopPropagation = (evt: React.MouseEvent<unknown>) => {
    evt.stopPropagation();
};
